"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function DemandForecastingMethodology() {
  const eyebrow = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const heading = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.3
  });
  const step1 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const step2 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const step3 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const line1 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const line2 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const steps = [{
    number: "01",
    title: "Analyze.",
    description: "We begin by auditing your historical performance and market positioning. By examining booking patterns, competitor rates, and local demand drivers, we establish a baseline for growth.",
    quote: "Data is the foundation of every profitable decision.",
    language: "Revenue Strategy"
  }, {
    number: "02",
    title: "Optimize.",
    description: "We implement dynamic pricing models and OTA listing strategies tailored to your property. Our focus is on capturing high-value segments while maintaining optimal rate parity.",
    quote: "Precision in pricing leads to peak occupancy.",
    language: "Revenue Management"
  }, {
    number: "03",
    title: "Scale.",
    description: "We continuously monitor performance metrics and adjust strategies in real-time. Through tech stack integration and ongoing audits, we ensure your revenue growth remains sustainable.",
    quote: "Consistency is the key to long-term success.",
    language: "Growth Consulting"
  }];
  return <section data-section-id="631" id="demandforecastingmethodology" className="bg-background text-foreground relative overflow-hidden py-32 md:py-40 lg:py-[160px] px-6 md:px-12">
      {}
      <div className="pointer-events-none absolute inset-0" style={{
      background: "radial-gradient(ellipse 80% 50% at 50% 0%, hsla(43, 65%, 35%, 0.08), transparent 70%)"
    }} aria-hidden="true" />

      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden select-none" aria-hidden="true">
        <span className="absolute top-[8%] left-[6%] font-serif italic" style={{
        fontSize: "180px",
        opacity: 0.03,
        color: "var(--foreground)"
      }}>
          R
        </span>
        <span className="absolute top-[35%] right-[8%] font-serif italic" style={{
        fontSize: "220px",
        opacity: 0.03,
        color: "var(--foreground)"
      }}>
          E
        </span>
        <span className="absolute bottom-[15%] left-[12%] font-serif italic" style={{
        fontSize: "200px",
        opacity: 0.03,
        color: "var(--foreground)"
      }}>
          V
        </span>
        <span className="absolute top-[60%] left-[45%] font-serif italic" style={{
        fontSize: "160px",
        opacity: 0.03,
        color: "var(--foreground)"
      }}>
          B
        </span>
      </div>

      <div className="relative max-w-7xl mx-auto">
        {}
        <div ref={eyebrow.ref} className={`text-center mb-6 transition-all duration-700 ease-out ${eyebrow.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="font-default text-xs md:text-sm tracking-[0.3em] uppercase" style={{
          color: "var(--secondary)"
        }}>
            The HotelRevBaBa Framework
          </span>
        </div>

        {}
        <h2 ref={heading.ref} className={`font-default text-center text-4xl md:text-5xl lg:text-6xl font-light leading-[1.1] tracking-tight max-w-3xl mx-auto transition-all duration-700 ease-out delay-100 ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Three pillars of{" "}
          <em className="italic font-normal" style={{
          color: "var(--secondary)"
        }}>
            revenue excellence.
          </em>
        </h2>

        {}
        <div className="relative mt-16 md:mt-24 lg:mt-[60px]">
          {}
          <div ref={step1.ref} data-index={0} className={`relative w-full md:w-[40%] mb-16 md:mb-0 transition-all duration-1000 ease-out ${step1.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <StepCard step={steps[0]} />
          </div>

          {}
          <div ref={line1.ref} className="hidden md:block absolute left-[35%] top-[180px] w-[30%] h-[120px] pointer-events-none" aria-hidden="true">
            <svg viewBox="0 0 300 120" fill="none" className="w-full h-full" preserveAspectRatio="none">
              <path d="M 10 20 Q 150 60, 290 100" stroke="var(--secondary)" strokeWidth="1" strokeDasharray="6 6" opacity="0.5" style={{
              strokeDashoffset: line1.isVisible ? 0 : 600,
              transition: "stroke-dashoffset 1.6s ease-out 0.3s"
            }} pathLength="600" />
            </svg>
          </div>

          {}
          <div ref={step2.ref} data-index={1} className={`relative w-full md:w-[50%] md:ml-auto md:mr-auto md:mt-[80px] mb-16 md:mb-0 transition-all duration-1000 ease-out ${step2.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <StepCard step={steps[1]} centered />
          </div>

          {}
          <div ref={line2.ref} className="hidden md:block absolute left-[55%] top-[480px] w-[30%] h-[120px] pointer-events-none" aria-hidden="true">
            <svg viewBox="0 0 300 120" fill="none" className="w-full h-full" preserveAspectRatio="none">
              <path d="M 10 20 Q 150 60, 290 100" stroke="var(--secondary)" strokeWidth="1" strokeDasharray="6 6" opacity="0.5" style={{
              strokeDashoffset: line2.isVisible ? 0 : 600,
              transition: "stroke-dashoffset 1.6s ease-out 0.3s"
            }} pathLength="600" />
            </svg>
          </div>

          {}
          <div ref={step3.ref} data-index={2} className={`relative w-full md:w-[40%] md:ml-auto md:mt-[160px] transition-all duration-1000 ease-out ${step3.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <StepCard step={steps[2]} alignRight />
          </div>
        </div>
      </div>
    </section>;
}
function StepCard({
  step,
  centered = false,
  alignRight = false
}: {
  step: {
    number: string;
    title: string;
    description: string;
    quote: string;
    language: string;
  };
  centered?: boolean;
  alignRight?: boolean;
}) {
  const alignment = alignRight ? "md:text-right md:items-end" : centered ? "md:text-left md:items-start" : "md:text-left md:items-start";
  return <div className={`flex flex-col ${alignment}`}>
      {}
      <div className="font-default leading-none mb-2 select-none" style={{
      fontSize: "clamp(120px, 18vw, 200px)",
      fontWeight: 600,
      color: "transparent",
      WebkitTextStroke: "1.5px var(--secondary)",
      letterSpacing: "-0.04em"
    }} aria-hidden="true">
        {step.number}
      </div>

      {}
      <h3 className="font-default text-3xl md:text-4xl lg:text-5xl font-medium mb-5" style={{
      color: "var(--foreground)"
    }}>
        {step.title}
      </h3>

      {}
      <p className="text-[17px] leading-relaxed max-w-md mb-6" style={{
      color: "var(--muted-foreground)"
    }}>
        {step.description}
      </p>

      {}
      <div className={`w-12 h-px mb-4 ${alignRight ? "md:ml-auto" : ""}`} style={{
      backgroundColor: "var(--secondary)",
      opacity: 0.5
    }} aria-hidden="true" />

      {}
      <div className={`max-w-sm ${alignRight ? "md:text-right" : ""}`}>
        <p className="font-default italic text-base md:text-lg leading-snug mb-1" style={{
        color: "var(--secondary)"
      }}>
          &ldquo;{step.quote}&rdquo;
        </p>
        <span className="font-default text-[11px] tracking-[0.2em] uppercase" style={{
        color: "var(--muted-foreground)"
      }}>
          {step.language}
        </span>
      </div>
    </div>;
}